import { Component, input, output, signal, computed, ElementRef, inject, ViewChild, TemplateRef, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Overlay, OverlayRef, OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

export interface SelectOption {
  value: string | number;
  label: string;
  sublabel?: string;
}

@Component({
  selector: 'custom-select',
  standalone: true,
  imports: [CommonModule, OverlayModule],
  templateUrl: './custom-select.component.html',
  host: {
    'class': 'inline-block'
  }
})
export class CustomSelectComponent {
  private readonly elementRef = inject(ElementRef);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private overlayRef: OverlayRef | null = null;

  @ViewChild('dropdownTemplate') dropdownTemplate!: TemplateRef<unknown>;
  @ViewChild('triggerButton') triggerButton!: ElementRef<HTMLButtonElement>;

  // Inputs
  readonly options = input<SelectOption[]>([]);
  readonly value = input<string | number | null | undefined>(null);
  readonly placeholder = input<string>('Select...');
  readonly disabled = input<boolean>(false);
  
  /** Size variant: 'xs' | 'sm' | 'md' | 'lg' */
  readonly size = input<'xs' | 'sm' | 'md' | 'lg'>('md');

  // Output
  readonly valueChange = output<string | number>();

  // Internal state
  readonly isOpen = signal(false);

  // Computed: find selected option
  readonly selectedOption = computed(() => {
    const val = this.value();
    if (val === null || val === undefined) return null;
    return this.options().find(opt => opt.value === val) || null;
  });

  // Computed: display text
  readonly displayText = computed(() => {
    const selected = this.selectedOption();
    return selected ? selected.label : this.placeholder();
  });

  // Computed: size classes for trigger button
  readonly triggerClasses = computed(() => {
    const base = 'w-full flex items-center justify-between gap-2 bg-white border rounded-lg transition-all duration-200 cursor-pointer';
    const focus = 'focus:outline-none focus:ring-2 focus:ring-brand-purple-500 focus:border-transparent';
    const hover = 'hover:border-gray-400';
    const disabled = this.disabled() ? 'opacity-50 cursor-not-allowed' : '';
    
    const sizes: Record<string, string> = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg'
    };
    
    const border = this.isOpen() ? 'border-brand-purple-500 ring-2 ring-brand-purple-500' : 'border-gray-300';
    
    return `${base} ${focus} ${hover} ${sizes[this.size()]} ${border} ${disabled}`;
  });

  toggle() {
    if (this.disabled()) return;
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.overlayRef) return;

    const positions: ConnectedPosition[] = [
      // Prefer below
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
      // Fallback above if no space below
      { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 }
    ];

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.triggerButton)
      .withPositions(positions)
      .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      width: this.triggerButton.nativeElement.offsetWidth,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop'
    });

    const portal = new TemplatePortal(this.dropdownTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);
    this.isOpen.set(true);

    // Close on backdrop click
    this.overlayRef.backdropClick().subscribe(() => this.close());
  }

  close() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.isOpen.set(false);
  }

  select(option: SelectOption) {
    this.valueChange.emit(option.value);
    this.close();
  }

  // Track function for @for
  trackByValue(index: number, option: SelectOption): string | number {
    return option.value;
  }
}
